import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiImage,
  FiDollarSign,
  FiInfo,
  FiEye,
  FiX,
  FiCopy,
} from "react-icons/fi";

const API_BASE = "https://api.aaveera365.in";

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [viewImageModal, setViewImageModal] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const initialFormState = {
    name: "",
    type: "bank", // 'bank' | 'upi' | 'qr_code'
    account_holder_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    upi_id: "",
    upi_holder_name: "",
    min_amount: "100",
    max_amount: "50000",
    instructions: "",
    status: "active",
    is_active: true,
    display_order: "0",
    qr_file: null,
  };

  const [form, setForm] = useState(initialFormState);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  /* ================= FETCH METHODS ================= */
  const loadMethods = async () => {
    setLoading(true);
    try {
      const res = await api.get("/payment-methods");
      setMethods(res.data?.data || []);
    } catch (err) {
      console.error("Error loading payment methods:", err);
      showToast(err.response?.data?.message || "Failed to load payment methods", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMethods();
  }, []);

  /* ================= FILE HANDLER ================= */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate image format
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      showToast("Only JPG, JPEG, PNG, or WEBP image formats are allowed", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size must be less than 5MB", "error");
      return;
    }

    setForm((prev) => ({ ...prev, qr_file: file }));
    setPreview(URL.createObjectURL(file));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      showToast("Please enter payment method name", "error");
      return;
    }

    if (form.type === "bank") {
      if (!form.account_holder_name.trim() || !form.bank_name.trim() || !form.account_number.trim() || !form.ifsc_code.trim()) {
        showToast("Please fill all required bank transfer fields", "error");
        return;
      }
    } else if (form.type === "upi") {
      if (!form.upi_id.trim()) {
        showToast("Please enter UPI ID", "error");
        return;
      }
    } else if (form.type === "qr_code") {
      if (!editingId && !form.qr_file) {
        showToast("Please upload a QR Code image", "error");
        return;
      }
    }

    setSubmitLoading(true);

    try {
      const payload = new FormData();
      // Universal fields for both legacy and new schemas
      payload.append("payment_settings_id", "8");
      payload.append("user_id", "2");
      payload.append("currency", "INR");
      payload.append("payment_type", form.type);
      payload.append("bank_account_number", form.account_number.trim());

      payload.append("name", form.name.trim());
      payload.append("type", form.type);
      payload.append("account_holder_name", (form.type === "upi" ? (form.upi_holder_name || form.account_holder_name) : form.account_holder_name).trim());
      payload.append("bank_name", form.bank_name.trim());
      payload.append("account_number", form.account_number.trim());
      payload.append("ifsc_code", form.ifsc_code.trim().toUpperCase());
      payload.append("branch_name", form.branch_name.trim());
      payload.append("upi_id", form.upi_id.trim());
      payload.append("upi_holder_name", form.upi_holder_name.trim());
      payload.append("min_amount", form.min_amount || "100");
      payload.append("max_amount", form.max_amount || "50000");
      payload.append("instructions", form.instructions.trim());
      payload.append("status", form.is_active ? "active" : "inactive");
      payload.append("is_active", form.is_active ? "true" : "false");
      payload.append("display_order", form.display_order || "0");

      if (form.qr_file) {
        payload.append("qr_file", form.qr_file);
      }

      if (editingId) {
        await api.put(`/payment-methods/${editingId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("Payment method updated successfully!");
      } else {
        await api.post("/payment-methods", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("Payment method created successfully!");
      }

      resetForm();
      loadMethods();
    } catch (err) {
      console.error("Submit error:", err);
      showToast(err.response?.data?.message || "Failed to save payment method", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initialFormState);
    setPreview(null);
    setEditingId(null);
  };

  /* ================= EDIT ================= */
  const handleEdit = (m) => {
    setEditingId(m.id);
    const imgUrl = getFullImageUrl(m.qr_image || m.qr_img);
    setPreview(imgUrl);

    const mType = m.type || m.payment_type || "bank";
    const mName = m.name || (mType === "bank" ? (m.bank_name ? `${m.bank_name} Bank Transfer` : "Bank Transfer") : mType === "upi" ? (m.upi_id ? `UPI (${m.upi_id})` : "UPI Payment") : "QR Code Payment");

    setForm({
      name: mName,
      type: mType,
      account_holder_name: m.account_holder_name || "",
      bank_name: m.bank_name || "",
      account_number: m.account_number || m.bank_account_number || "",
      ifsc_code: m.ifsc_code || "",
      branch_name: m.branch_name || "",
      upi_id: m.upi_id || "",
      upi_holder_name: m.upi_holder_name || m.account_holder_name || "",
      min_amount: m.min_amount?.toString() || "100",
      max_amount: m.max_amount?.toString() || "50000",
      instructions: m.instructions || "",
      status: m.status || (m.is_active ? "active" : "inactive"),
      is_active: m.is_active !== undefined ? m.is_active : m.status === "active",
      display_order: m.display_order?.toString() || "0",
      qr_file: null,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete payment method "${name}"?`)) return;

    try {
      await api.delete(`/payment-methods/${id}`);
      showToast("Payment method deleted successfully");
      loadMethods();
    } catch (err) {
      console.error("Delete error:", err);
      showToast(err.response?.data?.message || "Failed to delete payment method", "error");
    }
  };

  /* ================= TOGGLE STATUS ================= */
  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.patch(`/payment-methods/${id}/status`, { is_active: newStatus });
      showToast(`Payment method ${newStatus ? "activated" : "deactivated"} successfully`);
      loadMethods();
    } catch (err) {
      console.error("Status toggle error:", err);
      showToast(err.response?.data?.message || "Failed to update status", "error");
    }
  };

  const getFullImageUrl = (path) => {
    if (!path || path.includes("/null") || path.endsWith("undefined")) return null;
    if (path.startsWith("http")) return path;
    return `${API_BASE}${path}`;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border flex items-center gap-3 transition-all transform animate-bounce ${
            toast.type === "error"
              ? "bg-red-50 text-red-800 border-red-200"
              : "bg-emerald-50 text-emerald-800 border-emerald-200"
          }`}
        >
          {toast.type === "error" ? <FiXCircle className="w-5 h-5 text-red-500" /> : <FiCheckCircle className="w-5 h-5 text-emerald-500" />}
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Payment Methods</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage Bank Transfer, UPI, and QR Code manual payment methods for user deposits.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition shadow-sm"
        >
          <FiPlus className="w-4 h-4" />
          Add Payment Method
        </button>
      </div>

      {/* ================= FORM CARD ================= */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {editingId ? "Edit Payment Method" : "Add Payment Method"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {editingId
                ? "Update the details and configuration for this payment method."
                : "Configure bank accounts, UPI IDs, or QR codes that users can deposit to."}
            </p>
          </div>
          {editingId && (
            <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
              Editing ID #{editingId}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Top Row: Type & Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                Payment Method Type <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition bg-white"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="bank">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="qr_code">QR Code</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                Payment Method Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                placeholder={
                  form.type === "bank"
                    ? "e.g., HDFC Fast Deposit Mode 1"
                    : form.type === "upi"
                    ? "e.g., Google Pay UPI Direct"
                    : "e.g., Instant QR Code Payment"
                }
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
          </div>

          {/* DYNAMIC SECTION 1: BANK TRANSFER */}
          {form.type === "bank" && (
            <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                <FiDollarSign className="w-4 h-4 text-emerald-600" />
                Bank Account Details
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., HDFC Bank"
                    value={form.bank_name}
                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Account Holder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., Aaveera Enterprises Pvt Ltd"
                    value={form.account_holder_name}
                    onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm font-mono text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., 50200012345678"
                    value={form.account_number}
                    onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm font-mono uppercase text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., HDFC0001234"
                    value={form.ifsc_code}
                    onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Branch Name <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., Connaught Place, New Delhi"
                    value={form.branch_name}
                    onChange={(e) => setForm({ ...form, branch_name: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* DYNAMIC SECTION 2: UPI */}
          {form.type === "upi" && (
            <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                <FiDollarSign className="w-4 h-4 text-emerald-600" />
                UPI Account Details
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    UPI ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm font-mono text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., business@okaxis / paytmqr@paytm"
                    value={form.upi_id}
                    onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    UPI Holder Name <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., Aaveera Services"
                    value={form.upi_holder_name}
                    onChange={(e) => setForm({ ...form, upi_holder_name: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* DYNAMIC SECTION 3: QR CODE */}
          {form.type === "qr_code" && (
            <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                <FiImage className="w-4 h-4 text-emerald-600" />
                QR Code & Associated UPI
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    UPI ID <span className="text-gray-400 font-normal">(optional - shown below QR)</span>
                  </label>
                  <input
                    className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2 text-sm font-mono text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    placeholder="e.g., merchant@upi"
                    value={form.upi_id}
                    onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    If provided, users can also copy this UPI ID directly from the Deposit screen.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Upload QR Code Image <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 flex items-center gap-4">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition shadow-sm">
                      <FiImage className="w-4 h-4 text-gray-500" />
                      {preview ? "Change QR Image" : "Choose QR Image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                    <span className="text-xs text-gray-400">JPG, PNG, WEBP (Max 5MB)</span>
                  </div>

                  {preview && (
                    <div className="mt-3 flex items-center gap-3">
                      <img
                        src={preview}
                        alt="QR Preview"
                        className="h-24 w-24 object-contain rounded-lg border border-gray-200 bg-white p-1 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreview(null);
                          setForm({ ...form, qr_file: null });
                        }}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <FiX className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COMMON FIELDS: LIMITS & INSTRUCTIONS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Min Deposit (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                value={form.min_amount}
                onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Max Deposit (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                value={form.max_amount}
                onChange={(e) => setForm({ ...form, max_amount: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Display Order
              </label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition bg-white"
                value={form.is_active ? "active" : "inactive"}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}
              >
                <option value="active">Active (Visible to Users)</option>
                <option value="inactive">Inactive (Disabled)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <FiInfo className="w-3.5 h-3.5 text-gray-400" />
              Instructions for Users
            </label>
            <textarea
              rows={2}
              className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
              placeholder="e.g., Please enter your exact 12-digit UTR number after completing the payment. Screenshot must clearly show UTR and amount."
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            />
          </div>

          {/* Submit Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitLoading}
              className="px-6 py-2.5 bg-gray-900 hover:bg-black text-white text-sm font-semibold rounded-xl transition shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : editingId ? (
                "Update Payment Method"
              ) : (
                "Create Payment Method"
              )}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ================= PAYMENT METHODS LIST ================= */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-900">Configured Payment Methods</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Total {methods.length} payment method{methods.length === 1 ? "" : "s"} configured
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50/70 text-gray-600 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
              <tr>
                <th className="px-4 py-3.5">#</th>
                <th className="px-4 py-3.5">Method & Type</th>
                <th className="px-4 py-3.5">Account / Details</th>
                <th className="px-4 py-3.5">Limits (₹)</th>
                <th className="px-4 py-3.5">QR Image</th>
                <th className="px-4 py-3.5 text-center">Order</th>
                <th className="px-4 py-3.5 text-center">Status</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      Loading payment methods...
                    </div>
                  </td>
                </tr>
              ) : methods.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <div className="max-w-xs mx-auto">
                      <FiDollarSign className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="font-medium text-gray-600">No payment methods found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Create your first manual payment method using the form above.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                methods.map((m, index) => {
                  const mType = m.type || m.payment_type || "bank";
                  const mName = m.name || (mType === "bank" ? (m.bank_name ? `${m.bank_name} Bank Transfer` : "Bank Transfer") : mType === "upi" ? (m.upi_id ? `UPI (${m.upi_id})` : "UPI Payment") : "QR Code Payment");
                  const mAccNum = m.account_number || m.bank_account_number || "";
                  const mHolderName = m.account_holder_name || m.upi_holder_name || "";
                  const isActive = m.is_active !== undefined ? m.is_active : m.status === "active";
                  const imageUrl = getFullImageUrl(m.qr_image || m.qr_img);

                  return (
                    <tr key={m.id} className="hover:bg-gray-50/80 transition">
                      <td className="px-4 py-4 text-gray-500 font-mono text-xs">{index + 1}</td>

                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{mName}</div>
                        <div className="mt-0.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              mType === "bank"
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : mType === "upi"
                                ? "bg-purple-50 text-purple-700 border border-purple-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {mType === "bank" ? "Bank Transfer" : mType === "upi" ? "UPI" : "QR Code"}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-xs text-gray-700">
                        {mType === "bank" && (
                          <div className="space-y-0.5">
                            <div className="font-semibold text-gray-900">{m.bank_name || "Bank Account"}</div>
                            {mAccNum && <div>A/C: <span className="font-mono">{mAccNum}</span></div>}
                            {m.ifsc_code && <div>IFSC: <span className="font-mono text-gray-600">{m.ifsc_code}</span></div>}
                            {mHolderName && <div className="text-gray-500">Name: {mHolderName}</div>}
                            {m.branch_name && <div className="text-gray-400">Branch: {m.branch_name}</div>}
                          </div>
                        )}

                        {mType === "upi" && (
                          <div className="space-y-0.5">
                            <div>UPI: <span className="font-mono font-semibold text-gray-900">{m.upi_id}</span></div>
                            {mHolderName && <div className="text-gray-500">Holder: {mHolderName}</div>}
                          </div>
                        )}

                        {mType === "qr_code" && (
                          <div className="space-y-0.5">
                            {m.upi_id ? (
                              <div>UPI: <span className="font-mono text-gray-900">{m.upi_id}</span></div>
                            ) : (
                              <span className="text-gray-400">Scan QR Code directly</span>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4 text-xs">
                        <div className="font-medium text-gray-800">
                          ₹{Number(m.min_amount || 100).toLocaleString("en-IN")} - ₹{Number(m.max_amount || 50000).toLocaleString("en-IN")}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {imageUrl ? (
                          <div
                            onClick={() => setViewImageModal(imageUrl)}
                            className="relative group cursor-pointer inline-block"
                            title="Click to view full image"
                          >
                            <img
                              src={imageUrl}
                              alt="QR"
                              className="h-10 w-10 object-contain rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm group-hover:opacity-75 transition"
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/30 rounded-lg">
                              <FiEye className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4 text-center font-mono text-xs text-gray-600">
                        {m.display_order ?? 0}
                      </td>

                      <td className="px-4 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => toggleStatus(m.id, isActive)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition ${
                            isActive
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                              : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                          }`}
                          title={`Click to ${isActive ? "deactivate" : "activate"}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-red-500"}`} />
                          {isActive ? "Active" : "Disabled"}
                        </button>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => handleEdit(m)}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                            title="Edit"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleDelete(m.id, m.name)}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= IMAGE PREVIEW MODAL ================= */}
      {viewImageModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setViewImageModal(null)}
        >
          <div
            className="relative bg-white rounded-2xl p-4 max-w-lg w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800">QR Code Preview</h3>
              <button
                onClick={() => setViewImageModal(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-center bg-gray-50 rounded-xl p-4">
              <img
                src={viewImageModal}
                alt="QR Code Preview"
                className="max-h-96 max-w-full object-contain rounded-lg"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <a
                href={viewImageModal}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-black transition"
              >
                Open Original Image
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
